import type { DateDropdownValue, FromToDate } from '@/typings/dateSelector';

const DATE_RANGE_CONFIG = [
    { name: 'Today', daysAgo: 0, presetKey: 'today' },
    { name: 'Yesterday', daysAgo: 1, presetKey: 'yesterday' },
    { name: 'Last 7 days', daysAgo: 7, presetKey: '7d' },
    { name: 'Last 15 days', daysAgo: 15, presetKey: '15d' },
    { name: 'Last 30 days', daysAgo: 30, presetKey: '30d' }
] as const;

export interface DateSelectorConfig {
    useDateRanges?: boolean;
    availableMonthsNumber?: number;
    emptyOptionLabel?: string;
    extraOption?: string;
}

export interface DateSelectorOption {
    name: string;
    value?: FromToDate | 'custom';
    presetKey: string;
}

export interface SerializedOrderDateFilter {
    datePreset: string | null;
    dateFrom: string | null;
    dateTo: string | null;
}

export interface DeserializedOrderDateFilter {
    selectedDate: DateDropdownValue | null;
    fromDate: string | null;
    toDate: string | null;
}

const toDateOnly = (value: string): string => (value.length >= 10 ? value.slice(0, 10) : value);

const datesMatch = (aFrom: string, aTo: string, bFrom: string, bTo: string): boolean =>
    toDateOnly(aFrom) === toDateOnly(bFrom) && toDateOnly(aTo) === toDateOnly(bTo);

const pad = (n: number): string => String(n).padStart(2, '0');

export const supportsAllDateFilter = (config: DateSelectorConfig): boolean => !!config.emptyOptionLabel?.length;

const toDateDropdownValue = (option: DateSelectorOption): DateDropdownValue => ({
    name: option.name,
    value: option.value,
    presetKey: option.presetKey
});

export const isDateOptionSelected = (selectedDate: DateDropdownValue | null, option: DateSelectorOption): boolean => {
    if (!selectedDate) return false;
    if (selectedDate.presetKey) return selectedDate.presetKey === option.presetKey;
    if (selectedDate.value === undefined || option.value === undefined) {
        return selectedDate.value === option.value && selectedDate.name === option.name;
    }
    if (typeof selectedDate.value === 'string' || typeof option.value === 'string') {
        return selectedDate.value === option.value;
    }
    return datesMatch(selectedDate.value.from, selectedDate.value.to, option.value.from, option.value.to);
};

export enum DateFormat {
    MMMddYYYY,
    MMMdd,
    MMMYYYY,
    MMMddYYYYTime
}

export const formatDate = (date: Date | string, format = DateFormat.MMMddYYYY) => {
    if (!date) return '';

    const value = date instanceof Date ? date : new Date(date);
    const options: Record<DateFormat, Intl.DateTimeFormatOptions> = {
        [DateFormat.MMMYYYY]: { month: 'short', year: 'numeric' },
        [DateFormat.MMMdd]: { month: 'short', day: 'numeric' },
        [DateFormat.MMMddYYYY]: { month: 'short', day: 'numeric', year: 'numeric' },
        [DateFormat.MMMddYYYYTime]: {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        }
    };

    return new Intl.DateTimeFormat('en-US', options[format]).format(value);
};

export const dateToLocalISO = (date: Date) => {
    const pad = (n: string | number) => String(n).padStart(2, '0');
    return (
        date.getFullYear() +
        '-' +
        pad(date.getMonth() + 1) +
        '-' +
        pad(date.getDate()) +
        'T' +
        pad(date.getHours()) +
        ':' +
        pad(date.getMinutes()) +
        ':' +
        pad(date.getSeconds()) +
        '.' +
        String(date.getMilliseconds()).padStart(3, '0')
    );
};

const isRangeDateOption = (option: DateSelectorOption): option is DateSelectorOption & { value: FromToDate } =>
    option.presetKey.startsWith('range:') && !!option.value && typeof option.value !== 'string';

export const getDateRanges = (date = new Date()): DateDropdownValue[] => {
    return buildDateSelectorOptions({ useDateRanges: true }, date)
        .filter((option): option is DateSelectorOption & { value: FromToDate } => isRangeDateOption(option))
        .map((option) => toDateDropdownValue(option));
};

export const buildDateSelectorOptions = (config: DateSelectorConfig, date = new Date()): DateSelectorOption[] => {
    const now = new Date(date);
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const options: DateSelectorOption[] = [];

    if (config.useDateRanges) {
        const referenceDate = new Date(now);
        referenceDate.setHours(0, 0, 0, 0);

        const globalEndDate = new Date(now);
        globalEndDate.setHours(23, 59, 59, 999);

        for (const { name, daysAgo, presetKey } of DATE_RANGE_CONFIG) {
            const startDate = new Date(referenceDate);
            startDate.setDate(referenceDate.getDate() - daysAgo);
            const endDate = new Date(globalEndDate);
            endDate.setDate(globalEndDate.getDate() - (daysAgo === 1 ? 1 : 0));

            options.push({
                name,
                presetKey: `range:${presetKey}`,
                value: {
                    from: dateToLocalISO(startDate),
                    to: dateToLocalISO(endDate)
                }
            });
        }
    }

    const availableMonthsNumber = config.availableMonthsNumber ?? 12;
    for (let i = 0; i <= availableMonthsNumber; i++) {
        const month = currentMonth - i;
        const startOfMonth = new Date(currentYear, month, 1, 0, 0, 0, 0);
        const endOfMonth = new Date(currentYear, month + 1, 0, 23, 59, 59, 999);
        const isCurrentMonth = i === 0;

        options.push({
            name: isCurrentMonth ? 'Current Month' : formatDate(startOfMonth, DateFormat.MMMYYYY),
            presetKey: isCurrentMonth
                ? 'current-month'
                : `month:${startOfMonth.getFullYear()}-${pad(startOfMonth.getMonth() + 1)}`,
            value: {
                from: dateToLocalISO(startOfMonth),
                to: dateToLocalISO(endOfMonth)
            }
        });
    }

    if (config.emptyOptionLabel?.length) {
        options.push({
            name: config.emptyOptionLabel,
            presetKey: 'all'
        });
    }

    if (config.extraOption?.length) {
        options.push({
            name: config.extraOption,
            presetKey: 'custom',
            value: 'custom'
        });
    }

    return options;
};

const findOptionByPreset = (
    options: DateSelectorOption[],
    presetKey: string | null | undefined
): DateSelectorOption | null => {
    if (!presetKey) return null;
    return options.find((option) => option.presetKey === presetKey) ?? null;
};

const findOptionBySelectedDate = (
    selectedDate: DateDropdownValue | null,
    fromDate: string | null,
    toDate: string | null,
    options: DateSelectorOption[]
): DateSelectorOption | null => {
    if (!selectedDate) return null;

    if (selectedDate.presetKey) {
        return findOptionByPreset(options, selectedDate.presetKey);
    }

    if (selectedDate.value === undefined) {
        return findOptionByPreset(options, 'all');
    }

    if (selectedDate.value === 'custom') {
        if (!fromDate || !toDate) return findOptionByPreset(options, 'custom');
        return options.find((option) => option.presetKey === 'custom') ?? null;
    }

    const selectedValue = selectedDate.value;
    if (!selectedValue || typeof selectedValue === 'string') {
        return null;
    }

    return (
        options.find(
            (option) =>
                option.value &&
                typeof option.value !== 'string' &&
                datesMatch(option.value.from, option.value.to, selectedValue.from, selectedValue.to)
        ) ?? null
    );
};

const findOptionByDates = (
    dateFrom: string,
    dateTo: string,
    options: DateSelectorOption[]
): DateSelectorOption | null => {
    const fromIso = `${dateFrom}T00:00:00.000`;
    const toIso = `${dateTo}T23:59:59.999`;

    return (
        options.find(
            (option) =>
                option.value &&
                typeof option.value !== 'string' &&
                datesMatch(option.value.from, option.value.to, fromIso, toIso)
        ) ?? null
    );
};

export const serializeOrderDateFilter = (
    selectedDate: DateDropdownValue | null,
    fromDate: string | null,
    toDate: string | null,
    config: DateSelectorConfig
): SerializedOrderDateFilter => {
    const options = buildDateSelectorOptions(config);
    const matchedOption = findOptionBySelectedDate(selectedDate, fromDate, toDate, options);

    if (!matchedOption) {
        return { datePreset: null, dateFrom: null, dateTo: null };
    }

    if (matchedOption.presetKey === 'all') {
        return { datePreset: 'all', dateFrom: null, dateTo: null };
    }

    if (matchedOption.presetKey === 'custom') {
        return {
            datePreset: 'custom',
            dateFrom: fromDate,
            dateTo: toDate
        };
    }

    return { datePreset: matchedOption.presetKey, dateFrom: null, dateTo: null };
};

export const deserializeOrderDateFilter = (
    datePreset: string | null,
    dateFrom: string | null,
    dateTo: string | null,
    config: DateSelectorConfig
): DeserializedOrderDateFilter => {
    const options = buildDateSelectorOptions(config);

    if (datePreset === 'all') {
        if (!supportsAllDateFilter(config)) {
            return { selectedDate: null, fromDate: null, toDate: null };
        }

        const allOption = findOptionByPreset(options, 'all');
        return {
            selectedDate: allOption ? toDateDropdownValue(allOption) : null,
            fromDate: null,
            toDate: null
        };
    }

    if (datePreset === 'custom' && dateFrom && dateTo) {
        const customOption = findOptionByPreset(options, 'custom');
        return {
            selectedDate: customOption
                ? toDateDropdownValue(customOption)
                : { name: 'Custom', value: 'custom', presetKey: 'custom' },
            fromDate: dateFrom,
            toDate: dateTo
        };
    }

    const presetOption = findOptionByPreset(options, datePreset);
    if (presetOption) {
        return {
            selectedDate: toDateDropdownValue(presetOption),
            fromDate: null,
            toDate: null
        };
    }

    if (dateFrom && dateTo) {
        const matchedOption = findOptionByDates(dateFrom, dateTo, options);
        if (matchedOption) {
            if (matchedOption.presetKey === 'custom') {
                return {
                    selectedDate: toDateDropdownValue(matchedOption),
                    fromDate: dateFrom,
                    toDate: dateTo
                };
            }

            return {
                selectedDate: toDateDropdownValue(matchedOption),
                fromDate: null,
                toDate: null
            };
        }

        return {
            selectedDate: { name: 'Custom', value: 'custom', presetKey: 'custom' },
            fromDate: dateFrom,
            toDate: dateTo
        };
    }

    return { selectedDate: null, fromDate: null, toDate: null };
};
